import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { crmPatch, errMsg } from "@/services/crmApi";
import { text, fmtTs, toDate } from "@/lib/format";

// Joining requests from brugo.in "Join as a pro" (Firestore pro_signups/{+91…}).
// Each doc: name, phone, age, categories + category_names, id_type, id_number, photo_url,
// id_card_url, language, status, source, created_at. Staff follow up here and start onboarding.
export const SIGNUP_STATUSES = [["new", "New"], ["contacted", "Contacted"], ["verified", "Verified"], ["onboarded", "Onboarded"], ["rejected", "Rejected"]];
const LABEL = Object.fromEntries(SIGNUP_STATUSES);
const TONE = { new: "bg-amber-50 text-amber-800 border-amber-200", contacted: "bg-sky-50 text-sky-700 border-sky-200", verified: "bg-violet-50 text-violet-700 border-violet-200", onboarded: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-muted text-muted-foreground border-border" };
const Pill = ({ s }) => <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${TONE[s] || TONE.new}`}>{LABEL[s] || s}</span>;
const statusOf = (d) => String(d.status || "new").toLowerCase();
const nameOf = (d) => d.full_name || d.name || "";
const trades = (d) => (d.category_names?.length ? d.category_names : d.categories || []).join(", ") || d.trade || "";
const ID_LABEL = { aadhaar: "Aadhaar", pan: "PAN", driving_licence: "Driving licence" };
const masked = (n) => (n ? `${"•".repeat(Math.max(0, String(n).length - 4))}${String(n).slice(-4)}` : "");
const isPdf = (u) => /\.pdf(\?|$)|%2Epdf|application%2Fpdf/i.test(String(u || ""));

function Doc({ url, label, testid }) {
  if (!url) return <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">{label}: not added</div>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block rounded-lg border border-border overflow-hidden hover:border-primary" data-testid={testid}>
      {isPdf(url) ? <span className="flex h-32 items-center justify-center bg-muted"><Icons.FileText className="h-8 w-8 text-muted-foreground" /></span>
        : <img src={url} alt={label} className="w-full h-32 object-cover" />}
      <span className="block px-2 py-1.5 text-xs">{label} <Icons.ExternalLink className="inline h-3 w-3" /></span>
    </a>
  );
}

function SignupDrawer({ d }) {
  const nav = useNavigate();
  const [st, setSt] = useState(statusOf(d));
  const [note, setNote] = useState(d.cc_note || "");
  const [showId, setShowId] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (fields) => {
    setBusy(true);
    try { await crmPatch("pro_signups", d.id, fields); toast.success("Joining request updated"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const onSave = () => {
    const fields = {};
    if (st !== statusOf(d)) fields.status = st;
    if (note !== (d.cc_note || "")) fields.cc_note = note;
    if (!Object.keys(fields).length) return toast.info("Nothing changed");
    save(fields);
  };
  const startOnboarding = () => nav("/field/new-professional", { state: { fromSignup: { id: d.id, full_name: nameOf(d), phone: d.phone, categories: d.categories || [], category_names: d.category_names || [], profile_image_url: d.photo_url || "", id_type: d.id_type, id_number: d.id_number, id_doc_url: d.id_card_url || "" } } });
  return (
    <>
      <SheetHeader className="text-left">
        <SheetTitle className="font-heading">{text(nameOf(d))}</SheetTitle>
        <SheetDescription><a href={`tel:${d.phone}`} className="underline">{d.phone}</a> · joining request from brugo.in</SheetDescription>
      </SheetHeader>
      <div className="mt-4 space-y-4">
        <div className="rounded-lg border border-border p-3 grid grid-cols-2 gap-y-2 text-sm" data-testid="signup-details">
          <span className="text-muted-foreground">Age</span><span>{text(d.age)}</span>
          <span className="text-muted-foreground">Trades</span><span>{text(trades(d))}</span>
          <span className="text-muted-foreground">ID proof</span>
          <span className="flex items-center gap-1.5">{ID_LABEL[d.id_type] || text(d.id_type)} · <span className="font-mono">{showId ? d.id_number : masked(d.id_number)}</span>
            {d.id_number && <button onClick={() => setShowId((v) => !v)} className="text-xs text-primary underline">{showId ? "hide" : "show"}</button>}</span>
          <span className="text-muted-foreground">Language</span><span>{d.language === "hi" ? "Hindi" : "English"}</span>
          <span className="text-muted-foreground">Received</span><span>{fmtTs(d.created_at)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Doc url={d.photo_url} label="Profile photo" testid="signup-photo" />
          <Doc url={d.id_card_url} label="ID card" testid="signup-idcard" />
        </div>
        <div className="rounded-lg border border-border p-3 space-y-3">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Follow-up</p>
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={st} onValueChange={setSt}><SelectTrigger data-testid="signup-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SIGNUP_STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Internal note</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Called on…, verification visit on…" data-testid="signup-note" /></div>
          <Button onClick={onSave} disabled={busy} className="w-full" data-testid="signup-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
        <Button variant="outline" className="w-full gap-1.5" onClick={startOnboarding} data-testid="signup-start-onboarding"><Icons.UserPlus className="h-4 w-4" />Start onboarding with these details</Button>
        {d.professional_id && <p className="text-xs text-muted-foreground">Onboarded as professional <span className="font-mono">{d.professional_id}</span></p>}
      </div>
    </>
  );
}

export function ProSignups() {
  const rows = useCollection("pro_signups");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(null);
  const sorted = useMemo(() => [...rows].sort((a, b) => (toDate(b.created_at)?.getTime() || 0) - (toDate(a.created_at)?.getTime() || 0)), [rows]);
  const shown = sorted.filter((d) => (filter === "all" || (filter === "open" ? !["onboarded", "rejected"].includes(statusOf(d)) : statusOf(d) === filter))
    && (!q || JSON.stringify(d).toLowerCase().includes(q.toLowerCase())));
  const sel = rows.find((d) => d.id === open);
  return (
    <div data-testid="pro-signups">
      <p className="mb-3 text-xs text-muted-foreground">People who registered on brugo.in → Join as a pro (Firestore <span className="font-mono">pro_signups</span>). Call them, verify, then start onboarding to create their professional profile.</p>
      <Card>
        <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, trade…" className="max-w-sm" data-testid="signup-search" />
          <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-44" data-testid="signup-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="all">All</SelectItem>{SIGNUP_STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <span className="ml-auto text-xs text-muted-foreground font-mono">{shown.length} shown</span>
        </div>
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Trades</TableHead><TableHead>Age</TableHead><TableHead>Photo · ID</TableHead><TableHead>Status</TableHead><TableHead>Received</TableHead></TableRow></TableHeader>
          <TableBody>
            {shown.map((d) => (
              <TableRow key={d.id} className="cursor-pointer" onClick={() => setOpen(d.id)} data-testid={`signup-row-${d.id}`}>
                <TableCell className="text-sm font-medium">{text(nameOf(d))}</TableCell>
                <TableCell className="font-mono text-xs">{d.phone}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[240px] truncate">{text(trades(d))}</TableCell>
                <TableCell className="text-sm">{text(d.age)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{d.photo_url ? "photo" : "no photo"} · {d.id_card_url ? "ID card" : "no ID card"}</TableCell>
                <TableCell><Pill s={statusOf(d)} /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtTs(d.created_at)}</TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No joining requests{filter === "open" ? " waiting" : ""}.</TableCell></TableRow>}
          </TableBody>
        </Table></div>
      </Card>
      <Sheet open={!!sel} onOpenChange={(v) => !v && setOpen(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="signup-drawer">{sel && <SignupDrawer key={sel.id} d={sel} />}</SheetContent>
      </Sheet>
    </div>
  );
}
